import { Injectable, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, AbstractControl, FormControl } from '@angular/forms';
import { FieldConfig, StepConfig } from './field-config';
import { Subscription } from 'rxjs';
import { AttachmentService } from '../../../core/services/attachment.service';
import { firstValueFrom, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { getMainBusinessActivities, BusinessSectorEnum } from '../../../core/enums/company.enum';
import { NotifyService } from '../../../core/services/notify.service';

@Injectable({ providedIn: 'root' })
export class FormbuilderService implements OnDestroy {
  private subs: Subscription[] = [];

  private baseUrl = environment.baseUrl;

  constructor(
    private fb: FormBuilder,
    private attachmentSvc: AttachmentService,
    private httpClient: HttpClient,
    private notifyService: NotifyService,
  ) { }

  buildFormFromSteps(steps: StepConfig[]): FormGroup {
    const group: any = {};
    steps.forEach((step, index) => {
      group[`step${index}`] = this.buildGroup(step.fields);
    });
    return this.fb.group(group);
  }

  buildGroup(fields: FieldConfig[], skipVisibility = false): FormGroup {
    const g: any = {};
    fields.forEach((f) => {
      if (f.type === 'templateDownload') {
        return;
      }
      if (f.type === 'group') {
        g[f.name] = this.buildGroup(f.children || []);
      } else if (f.type === 'array') {
        const arrayControl = this.fb.array([]);
        g[f.name] = arrayControl;
        if (f.initialArrayItems && f.initialArrayItems > 0) {
          for (let i = 0; i < f.initialArrayItems; i++) {
            this.addArrayItem(arrayControl, f);
          }
        }
      } else {
        const shouldDisable = skipVisibility ? !!f.readOnly : !!f.readOnly || !!f.showIf;

        let initialValue: any;
        if (f.defaultValue !== undefined) {
          initialValue = f.defaultValue;
        } else if (f.type === 'select' && f.multiple) {
          initialValue = [];
        } else if (f.type === 'number' || f.type === 'select' || f.type === 'time') {
          initialValue = null;
        } else {
          initialValue = '';
        }
        g[f.name] = new FormControl(
          { value: initialValue, disabled: shouldDisable },
          f.validators || [],
          f.asyncValidators || [],
        );
      }
    });

    const form = this.fb.group(g);

    if (!skipVisibility) {
      fields.forEach((f) => this.fieldVisibility(f, form));
    }

    return form;
  }

  addArrayItem(control: FormArray, config: FieldConfig) {
    const group = this.buildGroup(config.children || [], true);
    control.push(group);

    if (config.children) {
      config.children.forEach((childField) => {
        const childControl = group.get(childField.name);
        if (childControl && !childField.readOnly && !childField.showIf) {
          childControl.enable({ emitEvent: false });
        }

        this.fieldVisibility(childField, group);
      });
      this.setupBusinessSectorActivityMap(config.children, group);
    }

    return group;
  }

  private setupBusinessSectorActivityMap(fields: FieldConfig[], group: FormGroup) {
    const businessSectorField = fields.find(f => f.name === 'businessSector');
    const mainActivityField = fields.find(f => f.name === 'mainBusinessActivity');

    if (businessSectorField && mainActivityField) {
      const businessSectorControl = group.get('businessSector');
      const mainActivityControl = group.get('mainBusinessActivity');

      if (businessSectorControl && mainActivityControl) {
        const updateDynamicOptions = (sector: BusinessSectorEnum | null) => {
          const options = getMainBusinessActivities(sector);
          (mainActivityControl as any)._dynamicOptions = options;
          return options;
        };

        const currentSector = businessSectorControl.value as BusinessSectorEnum;
        updateDynamicOptions(currentSector);

        const sub = businessSectorControl.valueChanges.subscribe((selectedSector: BusinessSectorEnum) => {
          const newOptions = updateDynamicOptions(selectedSector);

          if (selectedSector) {
            const currentValue = mainActivityControl.value;
            if (Array.isArray(currentValue)) {
              const validValues = currentValue.filter(val =>
                newOptions?.some(opt => opt.value === val)
              );
              if (validValues.length !== currentValue.length) {
                mainActivityControl.setValue(validValues.length > 0 ? validValues : [], { emitEvent: false });
              }
            } else {
              const isValidOption = newOptions?.some(opt => opt.value === currentValue);
              if (!isValidOption) {
                mainActivityControl.setValue(mainActivityField.multiple ? [] : null, { emitEvent: false });
              }
            }
          } else {
            mainActivityControl.setValue(mainActivityField.multiple ? [] : null, { emitEvent: false });
          }
        });

        this.subs.push(sub);
      }
    }
  }

  removeArrayItem(control: FormArray, index: number) {
    control.removeAt(index);
  }

  getFormArray(parent: AbstractControl | null, name: string): FormArray {
    return (parent?.get(name) as FormArray) || this.fb.array([]);
  }

  fieldVisibility(field: FieldConfig, parent: FormGroup) {
    if (field.type === 'templateDownload') return;

    if (field.showIf) {
      const currentControl = parent.get(field.name);
      if (!currentControl) return;

      // Handle array of conditions
      if (Array.isArray(field.showIf)) {
        const checkAllConditions = () => {
          return field.showIf && Array.isArray(field.showIf) && field.showIf.every(condition => {
            const targetControl = parent.get(condition.field);
            return targetControl && targetControl.value === condition.value;
          });
        };

        const initialVisible = checkAllConditions();
        if (initialVisible && !field.readOnly) {
          currentControl.enable({ emitEvent: false });
        } else {
          currentControl.disable({ emitEvent: false });
        }
        field.showIf.forEach(condition => {
          const targetControl = parent.get(condition.field);
          if (targetControl) {
            const sub = targetControl.valueChanges.subscribe(() => {
              const visible = checkAllConditions();
              if (visible && !field.readOnly) {
                currentControl.enable({ emitEvent: false });
              } else {
                currentControl.disable({ emitEvent: false });
                if (!field.readOnly) {
                  currentControl.reset('', { emitEvent: false });
                }
              }
            });
            this.subs.push(sub);
          }
        });
      } else {
        // Handle single condition
        const condition = field.showIf;
        const targetControl = parent.get(condition.field);

        if (targetControl) {
          const initialVisible = targetControl.value === condition.value;
          if (initialVisible && !field.readOnly) {
            currentControl.enable({ emitEvent: false });
          } else {
            currentControl.disable({ emitEvent: false });
          }

          const sub = targetControl.valueChanges.subscribe((value) => {
            const visible = value === condition.value;
            if (visible && !field.readOnly) {
              currentControl.enable({ emitEvent: false });
            } else {
              currentControl.disable({ emitEvent: false });
              if (!field.readOnly) {
                currentControl.reset('', { emitEvent: false });
              }
            }
          });
          this.subs.push(sub);
        }
      }
    }

    if (typeof field.visible === 'function') {
      const sub = parent.valueChanges.subscribe((val) => {
        const visible = (field.visible as any)(val);
        const ctl = parent.get(field.name);
        if (!ctl) return;
        if (visible) ctl.enable({ emitEvent: false });
        else ctl.disable({ emitEvent: false });
      });
      this.subs.push(sub);
    } else if (field.visible === false) {
      const c = parent.get(field.name);
      if (c) c.disable({ emitEvent: false });
    }
  }

  fileToBase64(file: File): Promise<{ base64: string; name: string; size: number; type: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve({ base64, name: file.name, size: file.size, type: file.type });
      };
      reader.readAsDataURL(file);
    });
  }

  generateReferenceNumber(serviceCode: string): Observable<any> {
    const url = `${this.baseUrl}applications/generate-reference-number`;
    const headers = new HttpHeaders({
      serviceCode: serviceCode,
    });
    return this.httpClient.get(url, { headers });
  }

  // Collect all attachment IDs
  collectAttachmentIds(formPayload: any): string[] {
    const attachmentIds: string[] = [];

    const extractId = (value: any): string | null => {
      if (!value) return null;
      if (typeof value === 'string') return null;
      if (typeof value === 'object' && value.attachmentId) {
        return typeof value.attachmentId === 'string' ? value.attachmentId : null;
      }
      return null;
    };

    const processObject = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;

      Object.values(obj).forEach((value: any) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            const id = extractId(item);
            if (id) attachmentIds.push(id);
            else if (typeof item === 'object') processObject(item);
          });
        } else {
          const id = extractId(value);
          if (id) attachmentIds.push(id);
          else if (typeof value === 'object' && value !== null) processObject(value);
        }
      });
    };

    Object.keys(formPayload).forEach((key) => {
      if (key === 'referenceNumber') return;
      processObject(formPayload[key]);
    });

    return attachmentIds;
  }

  removeFileObjects(payload: any): void {
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      if (value && typeof value === 'object' && value.attachmentId) {
        delete payload[key];
      } else if (Array.isArray(value)) {
        payload[key] = value.filter(
          (item: any) => !(item && typeof item === 'object' && item.attachmentId),
        );
        if (payload[key].length === 0) delete payload[key];
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }
}
